<template>
    <div v-if="isShowPopup && winner" class="jackpotWinnerPopUpWrapper">
        <div class="jackpotWinnerPopUp">
            <div class="jackpotWinnerPopUp_header" />
            <div class="jackpotWinnerPopUp_content">
                <i>
                    {{ i18n.t('jackpot_congratulations') }}
                </i>
                <I18nT
                    tag="div"
                    class="jackpotWinnerInfo"
                    keypath="jackpot_youWon"
                >
                    <template #jackpot>
                        <div class="highlight highlight-primary">
                            <strong>
                                {{ i18n.t(`jackpot_${winner.type.toLowerCase()}Jackpot`) }}
                            </strong>
                        </div>
                    </template>
                    <template #amount>
                        <strong class="highlight">
                            {{ formatMoneyWithSymbolAndDecimal(winner.amount) }}
                        </strong>
                    </template>
                </I18nT>
                <a
                    class="jackpotLinkTnC"
                    v-bind:href="tncLink"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {{ i18n.t('jackpot_tnc_link') }}
                </a>
                <button
                    class="claimButton"
                    type="button"
                    v-on:click="claim()"
                >
                    <strong>
                        {{ i18n.t('jackpot_claimNow') }}
                    </strong>
                </button>
            </div>
            <div class="bgDeco">
                <div class="ray ray-1" />
                <div class="ray ray-2" />
                <span
                    v-for="i in 20"
                    v-bind:key="i"
                    v-bind:class="`shiny shiny-${i}`"
                />
                <Vue3Lottie
                    v-if="!isJackpotEFootballInBigEventTheme"
                    class="bgDeco_coins"
                    v-bind:animationData="animationData"
                />
            </div>
        </div>
    </div>

    <FadeTransition>
        <Notification
            v-if="notification"
            class="notification"
            v-bind:type="notification.type"
            v-bind:icon="notification.icon"
        >
            {{ notification.message }}
        </Notification>
    </FadeTransition>
</template>

<script lang="ts" setup>
    import { autoResetRef } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import { computed, ref, watch, watchEffect } from 'vue';
    import { Vue3Lottie } from 'vue3-lottie';
    import Notification from '../common/Notification.vue';
    import FadeTransition from '../transitions/FadeTransition.vue';
    import animationData from '@/assets/images/jackpot/lottie/coinSpray.json';
    import { useWinner } from '@/composable/useJackpotData';
    import { usePlatform } from '@/composable/usePlatform';
    import { Api } from '@/core/lib/api';
    import { getJackpotTncUrl } from '@/core/lib/helpCenter';
    import { i18n } from '@/core/lib/i18n';
    import { TriggerResult } from '@/interface/jackpot';
    import { useCustomerStore } from '@/store/customerStore';
    import { useScrollableStore } from '@/store/scrollableStore';
    import { useToggleStore } from '@/store/toggleStore';

    const { winner } = useWinner();

    const { formatMoneyWithSymbolAndDecimal } = useCustomerStore();

    const { isJackpotEFootballInBigEventTheme } = storeToRefs(useToggleStore());

    if (isJackpotEFootballInBigEventTheme.value) {
        import('./JackpotWinnerPopupBigEvent.scss');
    } else {
        import('./JackpotWinnerPopup.scss');
    }

    const { setIsScrollable } = useScrollableStore();
    const isShowPopup = ref(false);
    watchEffect(() => {
        if (winner.value) {
            isShowPopup.value = true;
        }
    });

    watch(isShowPopup, (isShow) => {
        if (isShow) {
            setIsScrollable(false);
        } else {
            setIsScrollable(true);
        }
    });

    const notification = autoResetRef<{
        type: 'success' | 'info' | 'warning' | 'error';
        icon: string;
        message: string;
    } | null>(null, 5000);

    const { refreshBalanceAfterAction } = usePlatform();
    const claim = async () => {
        isShowPopup.value = false;

        try {
            if (!winner.value) throw new Error('No winner data');

            const triggerResult = await Api.claimJackpot(winner.value.type, winner.value.round);
            if (triggerResult !== TriggerResult.Success) throw new Error(`claimJackpot: ${TriggerResult[triggerResult]}`);

            notification.value = {
                type: 'success',
                icon: 'icon-system-checkCircle',
                message: i18n.t('jackpot_claimSuccess'),
            };
            refreshBalanceAfterAction();
        } catch (err) {
            console.error(err);

            notification.value = {
                type: 'error',
                icon: 'icon-system-warning',
                message: i18n.t('jackpot_tryAgainLater'),
            };
        }
    };

    const { brandType, isMainDomain } = storeToRefs(useCustomerStore());

    const tncLink = computed(() => getJackpotTncUrl(brandType.value, isMainDomain.value));
</script>
